import axios, { AxiosError } from 'axios';
import { signOut } from '@aws-amplify/auth';
import { getAuthHeader } from './api'; // Correct path relative to utils directory

// Prefer REACT_APP_API_URL over REACT_APP_API_BASE_URL, fallback to local API in development
const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.DEV ? 'http://localhost:3001' : '');
const TENANT_ORG_ID = import.meta.env.VITE_TENANT_ORG_ID || '';

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

// Request Interceptor to add Auth Header
axiosInstance.interceptors.request.use(
  async (config) => {
    const authHeader = await getAuthHeader();
    if (authHeader.Authorization) {
      config.headers.Authorization = authHeader.Authorization;
    }
    // Add tenant header for local development to resolve org explicitly
    try {
      const isDev = import.meta.env.DEV;
      const isLocalHost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      if (isDev && isLocalHost && TENANT_ORG_ID && config.url) {
        const url = new URL(config.url, config.baseURL || undefined);
        const path = url.pathname.replace(/\/+$/, '');
        if (path === '/tenant/config') {
          config.headers = { ...(config.headers || {}), 'x-tenant-org-id': TENANT_ORG_ID } as any;
        }
      }
    } catch {}
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);


// Response Error Interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    // If response is successful, just return it
    return response;
  },
  async (error: AxiosError) => {
    // Only sign out on 401 Unauthorized (token invalid/expired)
    // Do NOT sign out on 403 Forbidden (user is authenticated but lacks permission for that feature)
    if (error.response && error.response.status === 401) {
      // Determine the request path to allow exceptions (e.g., /tenant/config)
      try {
        const cfg: any = error.config || {};
        const urlObj = cfg.url ? new URL(cfg.url, cfg.baseURL || undefined) : undefined;
        const path = (urlObj?.pathname || '').replace(/\/+$/, '');
        const isTenantConfig = path === '/tenant/config';
        const hadAuthHeader = !!(cfg.headers && (cfg.headers.Authorization || cfg.headers.authorization));

        // Never force reload/sign-out for /tenant/config failures; let callers handle gracefully
        if (!isTenantConfig && hadAuthHeader) {
          console.warn(`Auth error (${error.response.status}) on ${path || cfg.url}. Signing out.`);
          try {
            await signOut();
            window.location.reload();
            return Promise.reject(error);
          } catch (signOutError) {
            console.error('Error during sign out:', signOutError);
          }
        }
      } catch {}
      // Fall through: propagate error without reload
    }
    // For all other errors (including 403 Forbidden), just reject the promise
    // This allows callers to handle permission errors gracefully without logging out
    return Promise.reject(error);
  }
);

export default axiosInstance; 